import React, { useEffect, useMemo, useState } from "react";
import "./docs.scss";

const LAST_UPDATED = "June 28, 2026";
const LAUNCHPAD_CONTRACT =
  process.env.REACT_APP_LAUNCHPAD_CONTRACT_ADDRESS ||
  "0xCfE813257aC0F0f7f7c1bFB005322Dc76d786b93";
const LAUNCHPAD_API_BASE = (process.env.REACT_APP_LAUNCHPAD_BACKEND_URL || "https://api.bobmonad.com").trim();
const MONAD_RPC = process.env.REACT_APP_MONAD_MAINNET_RPC_URL || "https://rpc.monad.xyz";
const MONAD_EXPLORER = "https://monadvision.com";

function isRootLaunchpadHost() {
  if (typeof window === "undefined") return false;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "gbob.fun" || host === "www.gbob.fun";
}

function getLaunchpadHref(mode = "list") {
  const rootHost = isRootLaunchpadHost();
  if (mode === "create") return rootHost ? "/create" : "/launchpad/create";
  if (mode === "profile") return rootHost ? "/profile" : "/launchpad/profile";
  return rootHost ? "/" : "/launchpad";
}

const WIKI_PAGES = [
  {
    slug: "home",
    group: "Getting Started",
    title: "Launchpad Home",
    subtitle: "Current launchpad docs for gbob.fun and the Bob launchpad routes on bobmonad.com.",
    updated: LAST_UPDATED,
    notice: "The Bob Launchpad is in beta. Ongoing updates and polish are still in progress.",
    quickLinks: [
      { title: "Presales", text: "Browse upcoming, live, completed, launched, and failed presales." },
      { title: "Create", text: "Configure token info, caps, timing, fee routing, whitelist mode, and dev buy." },
      { title: "Profile", text: "View connected wallet participation history and claimable presales." },
    ],
    sections: [
      {
        id: "overview",
        title: "Overview",
        text:
          "Bob Launchpad lets creators run MON-denominated presales on gbob.fun, then launch the token on Nad.fun after a successful sale. The flow is designed around transparent caps, live progress, wallet-linked participation, and configurable fee routing.",
      },
      {
        id: "routes",
        title: "Main Routes",
        bullets: [
          "gbob.fun uses / for the presale list, /create for presale creation, /profile for participation history, and /presales/:poolId for detail pages.",
          "bobmonad.com keeps the same launchpad under /launchpad, /launchpad/create, /launchpad/profile, and /launchpad/presales/:poolId.",
          "Support docs remain available from the Support entry in the launchpad navigation.",
        ],
      },
      {
        id: "launch-model",
        title: "Launch Model",
        bullets: [
          "Creators presale on gbob.fun and launch on Nad.fun after a successful raise.",
          "Presales can be public or whitelist-only.",
          "Soft cap is optional, hard cap is required, and min/max contribution are required.",
          "Hard cap plus dev buy is validated against the current Nad launch-safe maximum before creation.",
        ],
      },
    ],
  },
  {
    slug: "creating-a-presale",
    group: "User Guide",
    title: "Creating A Presale",
    subtitle: "What creators configure before submitting a new presale.",
    updated: LAST_UPDATED,
    sections: [
      {
        id: "required-fields",
        title: "Required Inputs",
        bullets: [
          "Token image, token name, token symbol, and description.",
          "Hard cap, minimum contribution, and maximum contribution.",
          "Start time and end time.",
          "At least one valid fee routing configuration if you want token trading fees routed after launch.",
        ],
      },
      {
        id: "optional-fields",
        title: "Optional Inputs",
        bullets: [
          "Soft cap if the presale should only succeed above a custom threshold.",
          "Dev buy if the creator wants to reserve part of the launch-safe buy amount upfront.",
          "Whitelist mode with uploaded wallet list and synced proofs.",
          "Website, X, Telegram, and other socials when available.",
        ],
      },
      {
        id: "fee-routing",
        title: "Fee Routing Options",
        bullets: [
          "Buyback & Burn buys back the launched token and burns it.",
          "LP Support strengthens liquidity after launch.",
          "Creator routes a share of trading fees to the creator configuration.",
          "Gift routes a share of trading fees to a designated recipient.",
          "Dividend routes a share of trading fees into up to 5 reward tokens for eligible holders.",
        ],
      },
      {
        id: "create-costs",
        title: "Creation Costs",
        bullets: [
          "The create flow includes the Bob platform fee quote returned by the fee service.",
          "The Nad deploy fee is 10 MON.",
          "Gas is separate and depends on network conditions.",
          "The summary panel shows the full creator upfront total before submission.",
        ],
      },
    ],
  },
  {
    slug: "participating-in-a-presale",
    group: "User Guide",
    title: "Participating In A Presale",
    subtitle: "What buyers should expect on live presales.",
    updated: LAST_UPDATED,
    sections: [
      {
        id: "before-buying",
        title: "Before Buying",
        bullets: [
          "Check the status, timing, raised amount, participants, and fee routing.",
          "Review soft cap, hard cap, min contribution, and max contribution.",
          "Confirm whether the sale is public or whitelist-only.",
          "Check the token description, socials, and creator address before participating.",
        ],
      },
      {
        id: "buyer-fees",
        title: "Buyer Fee",
        text:
          "Each presale participation currently adds a 2% platform fee on top of the entered allocation amount. If a buyer enters 100 MON, the allocation is 100 MON and the total transaction cost is 102 MON plus gas.",
      },
      {
        id: "claims-and-refunds",
        title: "Claims And Refunds",
        bullets: [
          "Successful presales move into Completed until the creator launches/finalizes the token.",
          "After launch, buyers can claim tokens from the detail page.",
          "Failed presales let buyers claim refunds.",
          "If a completed presale is not launched within the grace window, it can fail over so funds are not stuck indefinitely.",
        ],
      },
      {
        id: "profile-history",
        title: "Profile Page",
        text:
          "The Profile page shows presales the connected wallet participated in. Participation cards show wallet-specific bought amount instead of only the generic raised total.",
      },
    ],
  },
  {
    slug: "presale-states",
    group: "Reference",
    title: "Presale States",
    subtitle: "Public status names used across the list page, detail page, profile, and activity feed.",
    updated: LAST_UPDATED,
    sections: [
      {
        id: "status-reference",
        title: "Status Reference",
        cards: [
          { tone: "upcoming", title: "Upcoming", text: "The presale start time has not been reached yet." },
          { tone: "live", title: "Live", text: "The presale is actively accepting contributions." },
          { tone: "completed", title: "Completed", text: "The presale ended successfully and is waiting for creator launch/finalization." },
          { tone: "launched", title: "Launched", text: "The presale was finalized and the token launch completed on Nad.fun." },
          { tone: "failed", title: "Failed", text: "The presale did not complete successfully or timed out into refund mode." },
        ],
      },
    ],
  },
  {
    slug: "fees-and-distribution",
    group: "Reference",
    title: "Fees And Distribution",
    subtitle: "Current fee surfaces exposed in the create flow and public detail pages.",
    updated: LAST_UPDATED,
    sections: [
      {
        id: "platform-fees",
        title: "Platform Fees",
        bullets: [
          "Presale creation uses a Bob platform fee quote returned in MON by the backend fee service.",
          "Presale participation currently adds a 2% platform fee on top of the buyer allocation.",
          "Nad deploy fee is shown separately from the Bob platform fee.",
        ],
      },
      {
        id: "token-fee-routing",
        title: "Token Trading Fee Routing",
        stats: [
          { label: "Burn", value: "Optional" },
          { label: "LP Support", value: "Optional" },
          { label: "Creator", value: "Optional" },
          { label: "Gift", value: "Optional" },
          { label: "Dividend", value: "Optional" },
        ],
      },
      {
        id: "dividend-notes",
        title: "Dividend Notes",
        bullets: [
          "Dividend routing supports up to 5 reward tokens.",
          "Reward token rows can use MON, the launched token, or custom token addresses.",
          "The reward ratio split is configured inside the dividend section of the create form.",
        ],
      },
    ],
  },
  {
    slug: "contracts-and-network",
    group: "Administration",
    title: "Contracts And Network",
    subtitle: "Current launchpad network values and contract references.",
    updated: LAST_UPDATED,
    sections: [
      {
        id: "network",
        title: "Network",
        codeRows: [
          { label: "Chain", value: "Monad Mainnet" },
          { label: "Chain ID", value: "143" },
          { label: "Native Token", value: "MON" },
          { label: "RPC", value: MONAD_RPC, href: MONAD_RPC },
          { label: "Explorer", value: MONAD_EXPLORER, href: MONAD_EXPLORER },
        ],
      },
      {
        id: "contracts",
        title: "Contracts",
        codeRows: [{ label: "Active Launchpad Contract", value: LAUNCHPAD_CONTRACT }],
      },
    ],
  },
  {
    slug: "public-api",
    group: "Developers",
    title: "Public API",
    subtitle: "Public endpoints used by the launchpad frontend and activity feed.",
    updated: LAST_UPDATED,
    sections: [
      {
        id: "base-url",
        title: "Base URL",
        codeRows: [{ label: "API Base", value: LAUNCHPAD_API_BASE, href: LAUNCHPAD_API_BASE }],
      },
      {
        id: "endpoints",
        title: "Public Endpoints",
        links: [
          { title: "GET /api/launchpad/public/presales", text: "Returns the public presale list with live display state and metadata." },
          { title: "GET /api/launchpad/public/presales/:poolId", text: "Returns one presale by pool id using the active contract scope." },
          { title: "GET /api/launchpad/public/presales/:contractAddress/:poolId", text: "Legacy/scoped detail route for indexed contracts." },
          { title: "GET /api/launchpad/public/profile/:wallet", text: "Returns participated presales for a wallet." },
          { title: "GET /api/launchpad/public/activity", text: "Returns the recent launchpad activity feed used by the reel." },
        ],
      },
      {
        id: "backend-behavior",
        title: "Backend Behavior",
        bullets: [
          "The public backend persists cached list, metadata, and activity data so reloads are faster and process restarts do not wipe recent state.",
          "Activity responses are capped to the recent window used by the frontend reel.",
          "Presale pages and reels hydrate from backend data first, then refresh in the background.",
        ],
      },
    ],
  },
  {
    slug: "troubleshooting",
    group: "Support",
    title: "Troubleshooting",
    subtitle: "Common checks for creators, buyers, and launchpad operators.",
    updated: LAST_UPDATED,
    sections: [
      {
        id: "faq",
        title: "Common Questions",
        faqs: [
          {
            q: "Why does the token address say Pending launch?",
            a: "The presale can complete before the Nad.fun launch is finalized. Until that final step happens, the launched token address remains pending.",
          },
          {
            q: "Why can a hard cap be rejected before create?",
            a: "Bob Launchpad validates hard cap plus dev buy against the current Nad launch-safe maximum so the creator cannot over-configure the launch buy amount.",
          },
          {
            q: "Why can a buy fail on a whitelist presale?",
            a: "Whitelist presales require the buyer wallet to be in the uploaded whitelist set and to receive a valid proof from the backend.",
          },
          {
            q: "What should I check first if a presale looks wrong?",
            a: "Check the active network, current launchpad contract, start/end times, contribution rules, whitelist mode, and the public detail endpoint response for that pool.",
          },
        ],
      },
    ],
  },
];

function parseCurrentSlug() {
  if (typeof window === "undefined") return "home";
  const host = String(window.location.hostname || "").toLowerCase();
  const parts = String(window.location.pathname || "/")
    .split("/")
    .filter(Boolean);
  if (host === "docs.bobmonad.com") {
    return parts[0] || "home";
  }
  if (parts[0] === "docs") {
    return parts[1] || "home";
  }
  return "home";
}

function getDocsHref(slug) {
  if (typeof window === "undefined") return `/docs/${slug}`;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "docs.bobmonad.com" ? `/${slug}` : `/docs/${slug}`;
}

function CodeRow({ label, value, href }) {
  return (
    <div className="docs-code-row">
      <span>{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer">
          <code>{value}</code>
        </a>
      ) : (
        <code>{value}</code>
      )}
    </div>
  );
}

export default function DocsPage() {
  const [query, setQuery] = useState("");
  const [activeSlug, setActiveSlug] = useState(parseCurrentSlug);

  useEffect(() => {
    const onPopState = () => setActiveSlug(parseCurrentSlug());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const activePage = useMemo(
    () => WIKI_PAGES.find((page) => page.slug === activeSlug) || WIKI_PAGES[0],
    [activeSlug]
  );

  const filteredGroups = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const pages = normalized
      ? WIKI_PAGES.filter((page) => {
          const haystack = [
            page.group,
            page.title,
            page.subtitle,
            ...page.sections.map((section) => section.title),
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(normalized);
        })
      : WIKI_PAGES;

    return pages.reduce((acc, page) => {
      if (!acc[page.group]) acc[page.group] = [];
      acc[page.group].push(page);
      return acc;
    }, {});
  }, [query]);

  const openPage = (slug) => {
    const href = getDocsHref(slug);
    window.history.pushState({}, "", href);
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div id="bob-docs-page">
      <header className="docs-topbar">
        <div className="docs-topbar-brand">
          <span className="docs-topbar-mark" />
          <strong>Bob Wiki</strong>
        </div>
        <label className="docs-topbar-search" aria-label="Search docs">
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search..."
          />
        </label>
        <div className="docs-topbar-links">
          <a href={getLaunchpadHref("list")}>Presales</a>
          <a href={getLaunchpadHref("create")}>Create</a>
          <a href={getLaunchpadHref("profile")}>Profile</a>
        </div>
      </header>

      <main className="docs-layout">
        <aside className="docs-sidebar">
          <div className="docs-sidebar-head">
            <button type="button" className="docs-sidebar-home" onClick={() => openPage("home")}>
              Home
            </button>
            <button type="button" className="docs-sidebar-browse">
              Browse
            </button>
          </div>

          <div className="docs-sidebar-groups">
            {Object.entries(filteredGroups).map(([groupName, pages]) => (
              <section key={groupName} className="docs-sidebar-group">
                <p>{groupName}</p>
                <div className="docs-sidebar-links">
                  {pages.map((page) => (
                    <button
                      key={page.slug}
                      type="button"
                      className={page.slug === activePage.slug ? "is-active" : ""}
                      onClick={() => openPage(page.slug)}
                    >
                      {page.title}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </aside>

        <section className="docs-content">
          <div className="docs-content-side">
            <div className="docs-panel docs-toc-panel">
              <p className="docs-panel-title">Table of Contents</p>
              <div className="docs-toc-links">
                {activePage.sections.map((section) => (
                  <a key={section.id} href={`#${section.id}`}>
                    {section.title}
                  </a>
                ))}
              </div>
            </div>

            <div className="docs-panel docs-last-edited">
              <p className="docs-panel-title">Last Updated</p>
              <strong>Bob Launchpad Docs</strong>
              <span>{activePage.updated}</span>
            </div>
          </div>

          <article className="docs-article">
            <div className="docs-article-head">
              <div>
                <h1>{activePage.title}</h1>
                <p>{activePage.subtitle}</p>
              </div>
            </div>

            {activePage.notice ? (
              <div className="docs-notice">
                <span className="docs-notice-icon">i</span>
                <strong>{activePage.notice}</strong>
              </div>
            ) : null}

            {activePage.quickLinks ? (
              <section className="docs-article-section">
                <h2>Quick Start</h2>
                <div className="docs-link-grid">
                  {activePage.quickLinks.map((link) => (
                    <div key={link.title} className="docs-link-card">
                      <strong>{link.title}</strong>
                      <p>{link.text}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {activePage.sections.map((section) => (
              <section key={section.id} id={section.id} className="docs-article-section">
                <h2>{section.title}</h2>

                {section.text ? <p>{section.text}</p> : null}

                {section.bullets ? (
                  <ul className="docs-bullets">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}

                {section.links ? (
                  <div className="docs-link-grid">
                    {section.links.map((link) => (
                      <div key={link.title} className="docs-link-card">
                        <strong>{link.title}</strong>
                        <p>{link.text}</p>
                      </div>
                    ))}
                  </div>
                ) : null}

                {section.stats ? (
                  <div className="docs-stat-grid">
                    {section.stats.map((stat) => (
                      <div key={stat.label} className="docs-stat-card">
                        <span>{stat.label}</span>
                        <strong>{stat.value}</strong>
                      </div>
                    ))}
                  </div>
                ) : null}

                {section.cards ? (
                  <div className="docs-card-grid">
                    {section.cards.map((card) => (
                      <div key={card.title} className={`docs-tone-card is-${card.tone}`}>
                        <strong>{card.title}</strong>
                        <p>{card.text}</p>
                      </div>
                    ))}
                  </div>
                ) : null}

                {section.codeRows ? (
                  <div className="docs-code-grid">
                    {section.codeRows.map((row) => (
                      <CodeRow key={`${row.label}-${row.value}`} {...row} />
                    ))}
                  </div>
                ) : null}

                {section.faqs ? (
                  <div className="docs-faq-list">
                    {section.faqs.map((faq) => (
                      <div key={faq.q} className="docs-faq-card">
                        <strong>{faq.q}</strong>
                        <p>{faq.a}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ))}
          </article>
        </section>
      </main>
    </div>
  );
}
